import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { PlaylistItem } from '../../interfaces/playlists';
import { RootState } from '../store';
import { libraryTracksService } from '../../services/libraryTracks';
import { getFromLocalStorageWithExpiry } from '../../utils/localstorage';

type SyncStatus = 'idle' | 'loading' | 'succeeded' | 'failed';
type CacheStatus = 'idle' | 'loading' | 'complete';

type FetchLikeSongsResult = {
  items: PlaylistItem[];
  total: number;
  sessionId: string | null;
  nextCursor: string | null;
};

type FetchMoreResult = {
  items: PlaylistItem[];
  nextCursor: string | null;
  sessionId: string | null;
  commit: boolean;
  totalPersisted?: number;
};

type SyncLikedSongsResult = {
  status: 'completed' | 'skipped';
};

interface LikedSongsState {
  items: PlaylistItem[];
  total: number;
  syncStatus: SyncStatus;
  pageSessionId: string | null;
  pageCursor: string | null;
  isLoading: boolean;
  lastError: string | null;
  cachedOffset: number;
  cachedPageSize: number;
  cacheStatus: CacheStatus;
}

const initialState: LikedSongsState = {
  items: [],
  total: 0,
  syncStatus: 'idle',
  pageSessionId: null,
  pageCursor: null,
  isLoading: false,
  lastError: null,
  cachedOffset: 0,
  cachedPageSize: 0,
  cacheStatus: 'idle'
};

type SyncPagePayload = {
  items: PlaylistItem[];
  totalPersisted?: number;
  nextCursor: string | null;
  sessionId: string | null;
  commit: boolean;
};

const parseAddedAtMillis = (value: unknown): number | null => {
  if (typeof value !== 'string') return null;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const sortPlaylistItemsByAddedAt = (items: PlaylistItem[]): PlaylistItem[] =>
  items
    .slice()
    .sort((a, b) => {
      const aMillis = parseAddedAtMillis(a?.added_at);
      const bMillis = parseAddedAtMillis(b?.added_at);
      if (aMillis === null && bMillis === null) return 0;
      if (aMillis === null) return 1;
      if (bMillis === null) return -1;
      return bMillis - aMillis;
    });

const upsertPlaylistItems = (existing: PlaylistItem[], incoming: PlaylistItem[]): PlaylistItem[] => {
  if (!incoming.length) {
    return existing;
  }

  const incomingById = new Map<string, PlaylistItem>();
  for (const item of incoming) {
    const trackId = item?.track?.id;
    if (typeof trackId === 'string' && trackId.trim().length > 0) {
      incomingById.set(trackId, item);
    }
  }

  if (incomingById.size === 0) {
    return existing;
  }

  const filteredExisting = existing.filter((item) => {
    const trackId = item?.track?.id;
    if (typeof trackId !== 'string') return true;
    return !incomingById.has(trackId);
  });

  const combined = [...filteredExisting, ...incomingById.values()];
  return sortPlaylistItemsByAddedAt(combined);
};

const resolveAccessToken = (state: RootState): string | null => {
  const tokenCandidates: Array<string | undefined> = [
    state.auth.token,
    getFromLocalStorageWithExpiry('access_token') as string | undefined,
    getFromLocalStorageWithExpiry('public_access_token') as string | undefined
  ];
  const token = tokenCandidates.find(
    (candidate) => typeof candidate === 'string' && candidate.trim().length > 0
  );
  return token ? token.trim() : null;
};

const buildFetchResult = (
  items: PlaylistItem[],
  total: number,
  sessionId: string | null,
  nextCursor: string | null
): FetchLikeSongsResult => ({
  items,
  total,
  sessionId,
  nextCursor
});

const INITIAL_CACHED_PAGE_LIMIT = 200;
const SUBSEQUENT_CACHED_PAGE_LIMIT = 1000;

export const fetchLikeSongs = createAsyncThunk<FetchLikeSongsResult, void, { state: RootState }>(
  'likedSongs/fetchLikeSongs',
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();
    const userId = state.auth.user?.id?.trim();
    const accessToken = resolveAccessToken(state);

    if (!userId || !accessToken) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[likedSongs] Missing user or access token; skipping cached fetch');
      }
      return buildFetchResult([], 0, null, null);
    }

    thunkAPI.dispatch(
      syncLikedSongs({
        triggerFullSync: true
      })
    );

    const { items, totalPersisted } = await libraryTracksService.fetchCachedTracks({
      spotifyUserId: userId,
      accessToken,
      limit: INITIAL_CACHED_PAGE_LIMIT,
      offset: 0
    });

    const resolvedTotal =
      typeof totalPersisted === 'number' && Number.isFinite(totalPersisted)
        ? totalPersisted
        : items.length;

    const hasMore =
      items.length === INITIAL_CACHED_PAGE_LIMIT && items.length < resolvedTotal;

    thunkAPI.dispatch(
      likedSongsSlice.actions.applyCachedPage({
        items,
        totalPersisted: resolvedTotal,
        nextOffset: items.length,
        pageSize: INITIAL_CACHED_PAGE_LIMIT,
        hasMore
      })
    );

    if (hasMore) {
      void thunkAPI.dispatch(fetchRemainingCachedPages());
    }

    return buildFetchResult(items, resolvedTotal, null, null);
  }
);

export const fetchMore = createAsyncThunk<FetchMoreResult, void, { state: RootState }>(
  'likedSongs/fetchMore',
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();
    const userId = state.auth.user?.id?.trim();
    const accessToken = resolveAccessToken(state);
    const { pageCursor, pageSessionId } = state.likedSongs;

    if (!userId || !accessToken) {
      throw new Error('[likedSongs] fetchMore requires authenticated user');
    }

    const response = await libraryTracksService.fetchTracksPage({
      spotifyUserId: userId,
      accessToken,
      after: pageCursor ?? undefined,
      sessionId: pageSessionId ?? undefined
    });

    return {
      items: response.items,
      nextCursor: response.nextCursor,
      sessionId: response.sessionId,
      commit: response.commit,
      totalPersisted: response.totalPersisted
    };
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      if (
        state.likedSongs.pageCursor === null &&
        state.likedSongs.pageSessionId === null &&
        state.likedSongs.items.length >= state.likedSongs.total &&
        state.likedSongs.total > 0
      ) {
        return false;
      }
      return true;
    }
  }
);

export const syncLikedSongs = createAsyncThunk<
  SyncLikedSongsResult,
  { triggerFullSync?: boolean } | undefined,
  { state: RootState }
>(
  'likedSongs/syncLikedSongs',
  async (options, thunkAPI) => {
    const state = thunkAPI.getState();
    const userId = state.auth.user?.id?.trim();
    const accessToken = resolveAccessToken(state);

    if (!userId || !accessToken) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[likedSongs] Cannot sync without authenticated user');
      }
      return {
        status: 'skipped'
      };
    }

    let nextCursor: string | null = state.likedSongs.pageCursor;
    let sessionId: string | null = state.likedSongs.pageSessionId;
    let commitReached = false;
    const shouldTriggerFullSync = options?.triggerFullSync !== false;
    let fullSyncTriggered = false;

    while (!commitReached) {
      const response = await libraryTracksService.fetchTracksPage({
        spotifyUserId: userId,
        accessToken,
        after: nextCursor ?? undefined,
        sessionId: sessionId ?? undefined
      });

      if (!fullSyncTriggered && shouldTriggerFullSync) {
        fullSyncTriggered = true;
        void libraryTracksService
          .syncTracks({
            spotifyUserId: userId,
            accessToken
          })
          .catch((error) => {
            if (process.env.NODE_ENV === 'development') {
              console.warn('[likedSongs] syncTracks request failed', error);
            }
          });
      }

      const effectiveSessionId = response.sessionId ?? sessionId ?? null;
      const effectiveNextCursor = response.nextCursor ?? null;
      const commit = response.commit || response.nextCursor === null;

      thunkAPI.dispatch(
        likedSongsSlice.actions.applySyncPage({
          items: response.items,
          totalPersisted:
            typeof response.totalPersisted === 'number' && Number.isFinite(response.totalPersisted)
              ? response.totalPersisted
              : undefined,
          nextCursor: commit ? null : effectiveNextCursor,
          sessionId: commit ? null : effectiveSessionId,
          commit
        })
      );

      if (commit) {
        commitReached = true;
        nextCursor = null;
        sessionId = null;
        break;
      }

      if (!effectiveNextCursor || effectiveNextCursor === nextCursor) {
        break;
      }

      nextCursor = effectiveNextCursor;
      sessionId = effectiveSessionId;
    }

    return {
      status: 'completed'
    };
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      return state.likedSongs.syncStatus !== 'loading';
    }
  }
);

const likedSongsSlice = createSlice({
  name: 'likedSongs',
  initialState,
  reducers: {
    removeSong: (state, action: PayloadAction<{ id: string }>) => {
      const targetId = action.payload.id;
      const nextItems = state.items.filter((item) => item.track?.id !== targetId);
      if (nextItems.length !== state.items.length) {
        state.items = nextItems;
        state.total = Math.max(0, state.total - 1);
      }
    },
    applyCachedPage: (
      state,
      action: PayloadAction<{
        items: PlaylistItem[];
        totalPersisted: number;
        nextOffset: number;
        pageSize: number;
        hasMore: boolean;
      }>
    ) => {
      const { items, totalPersisted, nextOffset, pageSize, hasMore } = action.payload;
      state.items = upsertPlaylistItems(state.items, items);
      state.total = totalPersisted;
      state.cachedOffset = nextOffset;
      state.cachedPageSize = pageSize;
      state.cacheStatus = hasMore ? 'loading' : 'complete';
    },
    applySyncPage: (state, action: PayloadAction<SyncPagePayload>) => {
      const { items, totalPersisted, nextCursor, sessionId, commit } = action.payload;
      state.items = upsertPlaylistItems(state.items, items);
      if (totalPersisted !== undefined) {
        state.total = totalPersisted;
      } else {
        state.total = Math.max(state.total, state.items.length);
      }
      if (commit) {
        state.pageCursor = null;
        state.pageSessionId = null;
        state.syncStatus = 'succeeded';
      } else {
        state.pageCursor = nextCursor;
        state.pageSessionId = sessionId;
        state.syncStatus = 'loading';
      }
      state.lastError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLikeSongs.pending, (state) => {
        state.isLoading = true;
        state.lastError = null;
        state.cacheStatus = 'loading';
        state.cachedOffset = 0;
        state.cachedPageSize = INITIAL_CACHED_PAGE_LIMIT;
      })
      .addCase(fetchLikeSongs.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = sortPlaylistItemsByAddedAt(state.items);
        if (action.payload.total > 0 || state.total === 0) {
          state.total = action.payload.total;
        }
        state.pageSessionId = action.payload.sessionId;
        state.pageCursor = action.payload.nextCursor;
        if (state.syncStatus === 'loading') {
          // keep loading state until sync finishes
          return;
        }
        state.syncStatus = 'idle';
      })
      .addCase(fetchLikeSongs.rejected, (state, action) => {
        state.isLoading = false;
        state.lastError =
          typeof action.error?.message === 'string' ? action.error.message : 'Failed to load liked songs';
      });

    builder
      .addCase(fetchMore.fulfilled, (state, action) => {
        state.items = upsertPlaylistItems(state.items, action.payload.items);
        if (action.payload.totalPersisted !== undefined) {
          state.total = action.payload.totalPersisted;
        }
        if (action.payload.commit) {
          state.pageCursor = null;
          state.pageSessionId = null;
        } else {
          state.pageCursor =
            action.payload.nextCursor !== undefined ? action.payload.nextCursor : state.pageCursor;
          state.pageSessionId =
            action.payload.sessionId !== undefined ? action.payload.sessionId : state.pageSessionId;
        }
      })
      .addCase(fetchMore.rejected, (state, action) => {
        state.lastError =
          typeof action.error?.message === 'string'
            ? action.error.message
            : 'Failed to load additional liked songs';
      });

    builder
      .addCase(syncLikedSongs.pending, (state) => {
        state.syncStatus = 'loading';
        state.lastError = null;
      })
      .addCase(syncLikedSongs.fulfilled, (state, action) => {
        if (action.payload.status === 'skipped') {
          state.syncStatus = 'idle';
          return;
        }
        if (state.pageCursor === null && state.pageSessionId === null) {
          state.syncStatus = 'succeeded';
        }
      })
      .addCase(syncLikedSongs.rejected, (state, action) => {
        state.syncStatus = 'failed';
        state.lastError =
          typeof action.error?.message === 'string' ? action.error.message : 'Failed to sync liked songs';
      });
  }
});

const fetchRemainingCachedPages = createAsyncThunk<void, void, { state: RootState }>(
  'likedSongs/fetchRemainingCachedPages',
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();
    const userId = state.auth.user?.id?.trim();
    const accessToken = resolveAccessToken(state);

    if (!userId || !accessToken) {
      return;
    }

    let offset = state.likedSongs.cachedOffset;
    const pageSize = SUBSEQUENT_CACHED_PAGE_LIMIT;

    if (state.likedSongs.cacheStatus === 'complete') {
      return;
    }

    while (true) {
      if (thunkAPI.signal.aborted) {
        break;
      }

      const { items, totalPersisted } = await libraryTracksService.fetchCachedTracks({
        spotifyUserId: userId,
        accessToken,
        limit: pageSize,
        offset
      });

      const nextOffset = offset + items.length;
      const hasMore = items.length === pageSize && nextOffset < totalPersisted;

      thunkAPI.dispatch(
        likedSongsSlice.actions.applyCachedPage({
          items,
          totalPersisted,
          nextOffset,
          pageSize,
          hasMore
        })
      );

      if (!hasMore || items.length === 0) {
        break;
      }

      offset = nextOffset;
    }
  }
);

export const likedSongsActions = {
  ...likedSongsSlice.actions,
  fetchLikeSongs,
  fetchMore,
  syncLikedSongs,
  fetchRemainingCachedPages
};

export default likedSongsSlice.reducer;
